/* eslint no-underscore-dangle: ["error", { "allow": ["__sentry_xhr__"] }] */
import * as Sentry from '@sentry/react';

import * as API from 'api/api';
import * as logger from 'common/logger';
import * as object from 'common/object';
import * as string from 'common/string';
import * as utility from 'common/utility';
import { mandatory } from 'common/validation';

import { SENTRY_IGNORED_ERRORS } from 'common/config';
import {
  API_ERROR_MESSAGES,
  API_ERROR_TYPES,
  NON_API_ERROR_MESSAGES,
  NON_API_ERROR_TYPES,
} from 'common/constants';

export {
  cleanUserDetails,
  getErrorMessage,
  getErrorStatus,
  handleAPIError,
  isIgnoredError,
  stringifyError,
  stringifyReport,
};

function determineError(error) {
  // Return Cognito errors without further processing
  if (isCognitoError(error)) {
    return error;
  }
  // Return API errors without further processing
  if (isNetworkError(error)) {
    return error;
  }
  return utility.isNull(getErrorStatus(error)) ? handleJSError(error) : error;
}

function getErrorMessage(error) {
  if (!error) {
    return null;
  }
  if (typeof error === 'string' || error instanceof String) {
    return error;
  }
  if (error instanceof Error) {
    return error?.response?.data?.error?.message ?? error.message;
  }
  if (
    error?.error &&
    (typeof error.error === 'string' || error.error instanceof String)
  ) {
    return error.error;
  }
  if (error?.error?.message) {
    return error.error.message;
  }
  if (error?.message) {
    return error.message;
  }
  return '';
}

function getErrorStatus(error) {
  if (utility.isNull(error)) {
    return null;
  }
  if (utility.isDefined(error.status)) {
    return error.status;
  }
  if (error.response?.status) {
    return error.response.status;
  }
  if (
    !utility.isNull(error) &&
    utility.isDefined(error.code) &&
    error.code === 'ECONNABORTED'
  ) {
    return 408;
  }
  return null;
}

async function handleAPIError({
  originalError = mandatory('originalError'),
  errorLocation = mandatory('errorLocation'),
  args,
} = {}) {
  // Process JavaScript errors using the correct handler
  if (utility.isUndefined(originalError.config)) {
    return determineError(originalError);
  }

  const error = utility.cloneError(originalError); // Independent copy of original error

  // Handle revised structure of errors returned in Axios >= 0.19.0
  if (originalError.isAxiosError) {
    try {
      if (error.request) {
        error.request = JSON.parse(
          object.stringifyNonCircular(originalError.request)
        );
      }
    } catch {
      //
    }
    try {
      if (error.response) {
        error.response = JSON.parse(
          object.stringifyNonCircular(originalError.response)
        );
      }
    } catch {
      //
    }
  }

  // Strip out information which is duplicated elsewhere
  if (error?.config && error?.response && error?.response?.config) {
    delete error.config;
  }
  if (error?.request && error?.response && error?.response?.request) {
    delete error.request;
  }

  // Axios may return config.data already stringified, so convert it back
  // to an object so that we can strip any sensitive data from it if necessary
  if (
    error?.config &&
    error?.config?.data &&
    typeof error.config.data === 'string'
  ) {
    error.config.data = JSON.parse(error.config.data.trim());
  }
  if (
    error?.response &&
    error?.response?.config &&
    error?.response?.config?.data &&
    typeof error.response.config.data === 'string'
  ) {
    error.response.config.data = JSON.parse(error.response.config.data.trim());
  }

  // Ensure we log the original message
  error.origin = originalError.message;

  // Move keys in the __sentry_xhr__ structure up a level
  if (error?.request && error?.request?.__sentry_xhr__) {
    Object.keys(error.request.__sentry_xhr__).forEach(key => {
      error.request[key] = error.request.__sentry_xhr__[key];
    });
    delete error.request.__sentry_xhr__;
  }

  if (error.code === 'ECONNABORTED') {
    error.message = API_ERROR_MESSAGES.API_TIMEOUT;
    error.type = API_ERROR_TYPES.API_TIMEOUT;
  } else if (originalError.message === 'Network Error') {
    error.message = API_ERROR_MESSAGES.API_TERMINATION;
    error.type = API_ERROR_TYPES.API_TERMINATION;
  } else if (!error?.response) {
    logger.error({
      event: 'Unidentified Error',
      properties: {
        OriginalError: originalError,
      },
    });
    if (error.request && error.request?.status_code === 0) {
      // XHR status code 0
      error.message = `Code: X-${string.errorGuid()}. ${
        API_ERROR_MESSAGES.API_XHR
      }`;
      error.type = API_ERROR_TYPES.API_XHR;
    } else {
      error.message = `Code: H-${string.errorGuid()}. ${
        API_ERROR_MESSAGES.API_HTTP
      }`;
      error.type = API_ERROR_TYPES.API_HTTP;
    }
  } else if (error?.response?.status) {
    if (
      error.response.status === 500 ||
      !error?.response?.data ||
      !error?.response?.data?.error
    ) {
      error.message = `Code: H-${string.errorGuid()}. ${
        API_ERROR_MESSAGES.API_HTTP
      }`;
    } else {
      error.message = error.response.data.error.message;
    }
    error.type = API_ERROR_TYPES.API_HTTP;
  }

  const status = getErrorStatus(originalError);

  // Ignore unauthorised errors received when on the signin page (these could be caused
  // by API requests completing *after* the user has signed out) or when validating
  // a new email address
  const currentPath = window?.location?.pathname;
  if (status === 401 && currentPath === '/signin') {
    return null;
  }

  // Forbidden errors occur when a user no longer has permissions on an endpoint
  if (status === 403 && !utility.isRunningTests()) {
    logger.error({
      event: '403 Error',
      properties: {
        OriginalError: originalError,
      },
    });
    window.location.reload();
  }

  // Stringify config data
  if (error.config && typeof error.config?.data === 'object') {
    error.config.data = JSON.stringify(error.config.data);
  }
  if (error.response && typeof error.response?.config?.data === 'object') {
    error.response.config.data = JSON.stringify(error.response.config.data);
  }

  logger.error({
    event: 'API Layer Error',
    properties: {
      ErrorLocation: errorLocation,
      Arguments: object.stringifyKeys(args), // Stringify and strip certain keys first
    },
    error: object.stringifyKeys(error), // And here
  });

  // Pretty sure this shouldn't happen...
  if (typeof window !== 'undefined' && status === 401) {
    logger.error({
      event: '401 Error',
      properties: {
        ErrorLocation: errorLocation,
        Arguments: object.stringifyKeys(args),
        isRefreshingSession: window?.EBX?.isRefreshingSession ?? false,
      },
      error: object.stringifyKeys(error),
    });
  }

  if (error && error?.response) {
    if (error?.response?.data) {
      if (status === 500) {
        return {
          status: error.response.status,
          error: error.message,
        };
      }
      return {
        status: error.response.status,
        error: error.response.data.error,
      };
    }
    if (error?.response?.body) {
      return {
        status: error.response.status,
        error: error.response.body.error,
      };
    }
    return {
      status: error.response.status,
      error: error.response,
    };
  }

  return error;
}

async function handleJSError(originalError) {
  const error = utility.cloneError(originalError); // Independent copy of original error
  if (!utility.isNull(error) && typeof error !== 'string') {
    if (
      utility.isUndefined(error.message) ||
      (error.message.indexOf('Symbol.iterator') === -1 &&
        error.message.indexOf('EBX:INTERNAL') === -1)
    ) {
      // Capture raw error
      if (typeof Sentry !== 'undefined') {
        let version = '0.0.0';
        try {
          version = await API.getVersion();
        } catch {
          //
        }
        Sentry.configureScope(scope => {
          scope.setTag('version', version);
        });
        if (utility.isError(error)) {
          Sentry.captureException(error);
        } else if (utility.isErrorLike(error)) {
          Sentry.captureException(new EchoboxError(error));
        } else {
          try {
            Sentry.captureException(new Error(error));
          } catch {
            //
          }
        }
      }
      // Capture categorised error
      if (utility.isDefined(error.message)) {
        error.origin = error.message;
      }
      error.message = `Code: N-${string.errorGuid()}. ${
        NON_API_ERROR_MESSAGES.NON_API_GENERAL
      }`;
      error.type = NON_API_ERROR_TYPES.NON_API_GENERAL;
      logger.error({
        event: 'Unhandled JavaScript Error',
        error,
      });
    }
  }
  return error;
}

function isCognitoError(error) {
  return error?.code && error?.message && error?.name;
}

function isIgnoredError(event, hint) {
  /* Extract details from event */
  const exceptions = []; // Error messages extracted from exceptions
  const breadcrumbs = []; // Error messages extracted from breadcrumbs
  try {
    Object.keys(event.exception.values).forEach(key => {
      if (event.exception.values[key]?.value) {
        exceptions.push(event.exception.values[key].value);
      }
    });
  } catch {
    //
  }
  try {
    Object.keys(event.breadcrumbs).forEach(key => {
      if (event.breadcrumbs[key]?.message) {
        breadcrumbs.push(event.breadcrumbs[key].message);
      }
    });
  } catch {
    //
  }
  /* Extract details from hint */
  let message;
  let filename;
  let error;
  try {
    message = hint.originalException.message;
    filename = hint.originalException.filename;
    error = hint.originalException.error;
  } catch {
    //
  }

  /* Identify unwanted errors */
  let ignored = false;
  SENTRY_IGNORED_ERRORS.forEach(ignore => {
    let matches = false;
    if (error && ignore?.typeError) {
      matches = error instanceof TypeError;
    }
    if (!matches && filename && ignore?.filename) {
      matches = filename.match(ignore.filename);
    }
    if (!matches && message && ignore?.message) {
      matches = message === ignore.message;
    }
    if (!matches && exceptions && ignore?.message) {
      matches = exceptions.some(exception => exception.match(ignore.message));
    }
    if (!matches && breadcrumbs && ignore?.message) {
      matches = breadcrumbs.some(breadcrumb =>
        breadcrumb.match(ignore.message)
      );
    }
    if (matches) {
      ignored = true;
    }
  });

  return ignored;
}

function isNetworkError(error) {
  return (
    !utility.isNull(error) &&
    utility.isDefined(error.message) &&
    (error.message === API_ERROR_MESSAGES.API_INITIALISATION ||
      error.message === API_ERROR_MESSAGES.API_TIMEOUT ||
      error.message === API_ERROR_MESSAGES.API_TERMINATION ||
      error.message.indexOf(API_ERROR_MESSAGES.API_HTTP) !== -1 ||
      error.message === 'Network Error')
  );
}

function stringifyError(error, filter, space) {
  const plainObject = {};
  Object.getOwnPropertyNames(error).forEach(key => {
    plainObject[key] = error[key];
  });
  return JSON.stringify(plainObject, filter, space);
}

function stringifyReport(report) {
  const stringified = utility.cloneObject(report);

  // Stringify any Arguments entries that are objects
  // This includes things like Arguments.accountAPIIds, Arguments.identifiers,
  // Arguments.permissionsOnProperty and Arguments.propertyURNs which can account for
  // a significant number of different fields
  if (stringified?.Arguments) {
    Object.keys(stringified.Arguments).forEach(arg => {
      if (typeof stringified.Arguments[arg] === 'object') {
        try {
          stringified.Arguments[arg] = JSON.stringify(
            stringified.Arguments[arg]
          );
        } catch {
          //
        }
      }
    });
  }

  // Stringify any Error.config and Error.response.config/headers/request entries
  if (stringified?.Error) {
    if (stringified.Error?.config) {
      try {
        stringified.Error.config = JSON.stringify(stringified.Error.config);
      } catch {
        //
      }
    }
    if (stringified?.Error?.response) {
      if (stringified.Error.response?.config) {
        try {
          stringified.Error.response.config = JSON.stringify(
            stringified.Error.response.config
          );
        } catch {
          //
        }
      }
      if (stringified.Error.response?.headers) {
        try {
          stringified.Error.response.headers = JSON.stringify(
            stringified.Error.response.headers
          );
        } catch {
          //
        }
      }
      if (stringified.Error.response?.request) {
        try {
          stringified.Error.response.request = JSON.stringify(
            stringified.Error.response.request
          );
        } catch {
          //
        }
      }
    }
  }

  return stringified;
}

// Removes the user details from the url, and the transaction (which is optional as it is a field that is only present in Transaction objects, not Error event objects)
function cleanUserDetails(url, transaction) {
  let cleanedURL;
  let cleanedTransaction;

  // Unsubcribe page has user details as a query parameter as opposed to other pages which simply append the user email to the url.
  if (url.includes('unsubscribe')) {
    cleanedURL = string.removeQueryParam(url, 'sender');
    cleanedTransaction = transaction || null;
  } else if (
    ['success', 'verify', 'requested'].some(path => url.includes(path))
  ) {
    cleanedURL = url.slice(0, url.lastIndexOf('/'));
    cleanedTransaction = transaction
      ? transaction.slice(0, transaction.lastIndexOf('/'))
      : null;
  }

  return { cleanedURL, cleanedTransaction };
}

class EchoboxError extends Error {
  constructor(error) {
    super(error.message);
    this.name = this.constructor.name;
    this.stack = error.stack;
  }
}
